import { z } from 'zod';

export const addressFieldsSchema = z.object({
  fullName: z.string().min(1).max(120).trim(),
  phone: z.string().min(6).max(20).trim(),
  line1: z.string().min(1).max(160).trim(),
  line2: z.string().max(160).trim().optional().default(''),
  city: z.string().min(1).max(80).trim(),
  state: z.string().min(1).max(80).trim(),
  postalCode: z.string().min(3).max(16).trim(),
  country: z.string().length(2).default('IN'),
  // Filled in when the address was chosen from the autocomplete (see /places); optional, never required.
  // null clears them (e.g. when the customer edits the city, the pin no longer applies)
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  placeId: z.string().max(300).nullable().optional(),
  isDefaultShipping: z.boolean().optional(),
  isDefaultBilling: z.boolean().optional(),
});

/** A coordinate is only meaningful as a pair. */
const hasBothOrNeitherCoordinate = (a: { latitude?: number | null; longitude?: number | null }) =>
  (a.latitude == null) === (a.longitude == null);
const coordinatePairIssue = { message: 'latitude and longitude go together', path: ['latitude'] };

export const addressSchema = addressFieldsSchema.refine(hasBothOrNeitherCoordinate, coordinatePairIssue);
/** For PATCH: any subset of the fields. */
export const addressPatchSchema = addressFieldsSchema.partial().refine(hasBothOrNeitherCoordinate, coordinatePairIssue);

export const checkoutSchema = z.object({
  cartId: z.string().uuid(),
  // optional - order updates also go to the customer's verified phone, so email is only an extra
  email: z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), z.string().email().max(254).optional()),
  shippingAddress: addressSchema,
  billingAddress: addressSchema.optional(),
  billingSameAsShipping: z.boolean().default(true),
  shippingMethodId: z.string().min(1),
  couponCode: z.string().max(40).optional(),
  // Idempotency key for payment/order creation (spec section 9)
  idempotencyKey: z.string().uuid(),
  // "online" uses the store's configured provider (e.g. Razorpay); "cod" = pay on delivery
  paymentMethod: z.enum(['online', 'cod']).default('online'),
  notes: z.string().max(2000).optional(),
  // instructions for the seller, per item (variant id -> text): "make it in blue", "gift wrap it"
  itemNotes: z.record(z.string().uuid(), z.string().trim().max(500)).optional(),
  // the saved address the customer picked, if they picked one
  addressId: z.string().uuid().optional(),
  // a new address is kept on the account unless they untick it
  saveAddress: z.boolean().optional(),
  makeDefaultAddress: z.boolean().optional(),
});

/** Client posts this after the Razorpay Checkout handler fires. */
export const razorpayVerifySchema = z.object({
  orderId: z.string().min(1), // our order id or number
  razorpayOrderId: z.string().min(1),
  razorpayPaymentId: z.string().min(1),
  razorpaySignature: z.string().min(1),
});

export type RazorpayVerifyInput = z.infer<typeof razorpayVerifySchema>;

export const customOrderRequestSchema = z.object({
  productType: z.string().min(1).max(60),
  contactName: z.string().min(1).max(120).trim(),
  email: z.string().email().max(254),
  phone: z.string().min(6).max(20).trim(),
  description: z.string().min(10).max(5000),
  targetSize: z.string().max(120).optional(),
  quantity: z.number().int().min(1).max(100000).optional(),
  deadline: z.string().datetime().optional(),
  // Private uploads are finalized media ids (never public)
  attachmentMediaIds: z.array(z.string().uuid()).max(10).optional(),
});

export type AddressInput = z.infer<typeof addressSchema>;
export type CheckoutInput = z.infer<typeof checkoutSchema>;
export type CustomOrderRequestInput = z.infer<typeof customOrderRequestSchema>;
